import Service from 'react-reservice';

import { EChartPeriod } from '@aqx-core/common/api/chart';
import { ITechnicalIndicator } from '@aqx-core/common/api/user-account-setting';
import { companyService } from '@aqx-core/common/services/company';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { DEFAULT_CANDLE_STICK_COLORS_RGB } from '@aqx-core/common/theme';

import { EChartProvider, IOrderTracker } from './types';
import { ICandlestickColorUserPreference } from './types/candlestick';
import { EChartSeries } from './types/series';
import { getDefaultChartPeriod } from './utils';

export interface IEquityChartServiceContext {
  askPriceVisible: boolean;
  technicalIndicators: ITechnicalIndicator[];
  selectedPeriod: EChartPeriod;
  selectedSeries: EChartSeries;
  orderTrackers: IOrderTracker[];
  // For Centroid: need to await socket result to close order tracker
  pendingCloseOrderTrackerId: string | undefined;
  useNewDataAsOpen: boolean;
  fetchingMore: boolean;
  isFullScreen: boolean;
  selectedProvider: EChartProvider;
  loading?: boolean;
  activeSymbolId?: string;
  previousAccessedSymbolId?: string;
  loadingIndicator?: boolean;
  // ===== Candlestick Setting =====
  isCandlestickFetching: boolean;
  isCandlestickUpdating: boolean;
  candlestickPreference: ICandlestickColorUserPreference;
}

const defaultContext: IEquityChartServiceContext = {
  askPriceVisible: storage.getItem(EStorageKey.ASK_PRICE_VISIBLE) || false,
  technicalIndicators: [],
  selectedPeriod: getDefaultChartPeriod(companyService.getCompanyIntegration()),
  selectedSeries: EChartSeries.CANDLESTICK,
  orderTrackers: [],
  pendingCloseOrderTrackerId: undefined,
  useNewDataAsOpen: false,
  fetchingMore: false,
  selectedProvider: EChartProvider.ORIGINAL,
  // Indicator Configuration
  isFullScreen: false,
  loadingIndicator: false,
  // ===== Candlestick Setting =====
  isCandlestickFetching: false,
  isCandlestickUpdating: false,
  candlestickPreference: { ...DEFAULT_CANDLE_STICK_COLORS_RGB }
};

class EquityChartService extends Service<IEquityChartServiceContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  getIsFullScreen = () => {
    return this.context.isFullScreen;
  };

  getSelectedProvider = () => {
    return this.context.selectedProvider;
  };

  setLoading = (loading: boolean) => {
    this.context.loading = loading;
  };

  setAskPriceVisible = (askPriceVisible: boolean) => {
    this.context.askPriceVisible = askPriceVisible;
  };

  setTechnicalIndicators = (technicalIndicators: ITechnicalIndicator[]) => {
    this.context.technicalIndicators = technicalIndicators;
  };

  setSelectedPeriod = (period: EChartPeriod) => {
    this.context.selectedPeriod = period;
  };

  setSelectedSeries = (series: EChartSeries) => {
    this.context.selectedSeries = series;
  };

  setOrderTrackers = (orderTrackers: IOrderTracker[]) => {
    this.context.orderTrackers = orderTrackers;
  };

  setPendingCloseOrderTrackerId = (orderId: string | undefined) => {
    this.context.pendingCloseOrderTrackerId = orderId;
  };

  setUseNewDataAsOpen = (useNewDataAsOpen: boolean) => {
    this.context.useNewDataAsOpen = useNewDataAsOpen;
  };

  setFetchingMore = (fetchingMore: boolean) => {
    this.context.fetchingMore = fetchingMore;
  };

  setIsFullScreen = (isFullScreen: boolean) => {
    this.context.isFullScreen = isFullScreen;
  };

  setSelectedProvider = (selectedProvider: EChartProvider) => {
    this.context.selectedProvider = selectedProvider;
  };

  setLoadingIndicator = (loadingIndicator: boolean) => {
    this.context.loadingIndicator = loadingIndicator;
  };

  // ===== Candlestick Setting =====

  setIsCandlestickFetching = (isLoading: boolean) => {
    this.context.isCandlestickFetching = isLoading;
  };

  setIsCandlestickUpdating = (isLoading: boolean) => {
    this.context.isCandlestickUpdating = isLoading;
  };

  setCandlestickPreference = (preference: ICandlestickColorUserPreference) => {
    this.context.candlestickPreference = { ...this.context.candlestickPreference, ...preference };
  };

  resetPeriod = () => {
    this.context = {
      ...defaultContext,
      selectedPeriod: getDefaultChartPeriod(companyService.getCompanyIntegration())
    };
  };
}

export const equityChartService = new EquityChartService();
